import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import LandingPage from "./pages/LandingPage";
import LandingLite from "./pages/LandingLite";
import Dashboard from "./pages/Dashboard";
import AdminLogin from "./pages/AdminLogin";
import AdminResetPassword from "./pages/AdminResetPassword";
import AdminDashboard from "./pages/AdminDashboard";
import AdminSettings from "./pages/AdminSettings";
import AdminUtilities from "./pages/AdminUtilities";
import { PrivacyPage, TermsPage } from "./pages/LegalPage";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Full-screen loader used while OAuth session exchange is in flight.
// Important: we render ONLY this — not <Routes> — so the browser never paints
// the marketing landing for a frame between the OAuth redirect and the
// session-data response (the cause of the "flicker to main site" complaint).
const AuthBlockingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand-accent mx-auto mb-4"></div>
      <p className="text-brand-ink-muted">Signing you in…</p>
    </div>
  </div>
);

// Detect "we just came back from the OAuth provider" so the first render of
// the SPA can hide all routes until the session is exchanged.
const hasPendingOAuth = () => typeof window !== 'undefined' && window.location.hash.includes('session_id=');

// Handles the OAuth callback (when the URL has #session_id=…). Owns the
// blocking state for the whole app — while authPending is true, App renders
// only the spinner.
const AuthHandler = ({ authPending, setAuthPending }) => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleAuth = async () => {
      // Skip auth handling for admin routes
      if (location.pathname.startsWith('/admin')) {
        setAuthPending(false);
        return;
      }

      // Check for session_id in URL fragment
      const sessionIdMatch = location.hash.match(/session_id=([^&]+)/);

      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        // Read any pending org_code stashed by /lite/<code> landing
        const pendingOrgCode = sessionStorage.getItem('pending_org_code');

        try {
          const headers = { "X-Session-ID": sessionId };
          if (pendingOrgCode) headers["X-Org-Code"] = pendingOrgCode;
          const response = await axios.get(`${API}/auth/session-data`, { headers });

          if (pendingOrgCode) sessionStorage.removeItem('pending_org_code');

          // Store session token in cookie (samesite=lax so cross-site
          // links — Gmail password-reset links, OAuth redirects, Stripe
          // success URLs — don't strip the cookie on first navigation)
          document.cookie = `session_token=${response.data.session_token}; path=/; max-age=${7*24*60*60}; samesite=lax; secure`;
          // Store user BEFORE we clear the spinner so the Dashboard's
          // lazy-init reads the right app_mode.
          localStorage.setItem('user', JSON.stringify(response.data.user));

          window.history.replaceState({}, document.title, "/dashboard");
          navigate('/dashboard', { replace: true });
        } catch (error) {
          console.error('Auth error:', error);
          // On failure, send lite invitees back to their invite page (not the
          // full marketing site), full users back to /.
          const pendingFallback = pendingOrgCode
            ? `/lite/${encodeURIComponent(pendingOrgCode)}`
            : '/';
          if (pendingOrgCode) sessionStorage.removeItem('pending_org_code');
          navigate(pendingFallback, { replace: true });
        } finally {
          setAuthPending(false);
        }
      } else {
        // No session_id in URL — quick "already signed in?" auto-redirect from /
        const user = localStorage.getItem('user');
        if (user && location.pathname === '/') {
          navigate('/dashboard', { replace: true });
        }
        setAuthPending(false);
      }
    };

    handleAuth();
  }, [location, navigate, setAuthPending]);

  if (authPending) return <AuthBlockingSpinner />;
  return null;
};

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const user = localStorage.getItem('user');
  return user ? children : <Navigate to="/" replace />;
};

const AppShell = () => {
  // Start blocked if the URL says we're mid-OAuth-callback. Otherwise the
  // user is on a normal page and we shouldn't show a spinner at all.
  const [authPending, setAuthPending] = useState(() => hasPendingOAuth());

  return (
    <>
      <AuthHandler authPending={authPending} setAuthPending={setAuthPending} />
      {/* While we're mid-OAuth-callback, render ONLY the spinner so the
          marketing landing never flashes between the redirect and the
          session exchange. */}
      {!authPending && (
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/lite/:code" element={<LandingLite />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/reset-password" element={<AdminResetPassword />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute>
                <AdminSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/utilities"
            element={
              <ProtectedRoute>
                <AdminUtilities />
              </ProtectedRoute>
            }
          />
        </Routes>
      )}
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
